import { useState, useEffect, useRef, type Ref } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { Button, Icon } from "@elaniintc/goes-design-system";
import ConfigurableMenuBar from "./ConfigurableMenuBar";
import SlotModal from "./SlotModal";
import { SlotMark } from "./SlotExtension";
import type { SlotType } from "@/types/entities/templates";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";

interface TemplateEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  maxHeight?: string;

  showSlots?: boolean;
  showHeadings?: boolean;
  showTables?: boolean;
  showAlignment?: boolean;
  showIndent?: boolean;
  integrated?: boolean;

  onSlotsChange?: (slots: Map<string, SlotType>) => void;

  templateType?: "email" | "document";
  subjectValue?: string;
  fileNameValue?: string;
  onSubjectChange?: (subject: string) => void;
  onFileNameChange?: (fileName: string) => void;

  versiones?: { label: string; value: string }[] | null;
  selectedVersion?: string;
  onVersionChange?: (versionId: string) => void;

  editorRef?: Ref<Editor>;
}

export default function TemplateEditor({
  value,
  onChange,
  minHeight = "300px",
  maxHeight = "600px",
  showSlots = false,
  showHeadings = true,
  showAlignment = true,
  showTables = true,
  integrated = false,
  onSlotsChange,
  templateType,
  subjectValue = "",
  onSubjectChange,
  versiones = null,
  selectedVersion,
  onVersionChange,
  editorRef,
}: TemplateEditorProps) {
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [usedSlots, setUsedSlots] = useState<Map<string, SlotType>>(new Map());
  const [slotToEdit, setSlotToEdit] = useState<{
    name: string;
    type: SlotType;
    from: number;
    to: number;
  } | null>(null);

  const isInitialMount = useRef(true);
  const previousValue = useRef(value);

  const extensions = [
    StarterKit.configure({
      paragraph: {
        HTMLAttributes: {
          class: "text-body",
        },
      },
      bold: {
        HTMLAttributes: {
          class: "text-body-loud",
        },
      },
      heading: {
        HTMLAttributes: {
          class: "text-heading",
        },
      },
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: "text-action underline cursor-pointer",
      },
    }),
    TextAlign.configure({
      types: ["heading", "paragraph"],
      alignments: ["left", "center", "right", "justify"],
    }),
    ...(showTables
      ? [
          Table.configure({
            resizable: true,
            HTMLAttributes: {
              class: "border-collapse table-auto w-full",
            },
          }),
          TableRow.configure({
            HTMLAttributes: {
              class: "border border-neutral-quiet",
            },
          }),
          TableHeader.configure({
            HTMLAttributes: {
              class:
                "border border-neutral-quiet bg-neutral-silent px-4 py-2 text-left font-semibold",
            },
          }),
          TableCell.configure({
            HTMLAttributes: {
              class: "border border-neutral-quiet px-4 py-2",
            },
          }),
        ]
      : []),
    ...(showSlots ? [SlotMark] : []),
  ];

  const editor = useEditor({
    extensions,
    content: value,
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none p-6`,
        style: `min-height: ${minHeight}; max-height: ${maxHeight}; overflow-y: auto;`,
      },
      transformPastedHTML: (html: string) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const allowedTags = [
          "p",
          "br",
          "strong",
          "b",
          "em",
          "i",
          "u",
          "s",
          "strike",
          "h1",
          "h2",
          "h3",
          "ul",
          "ol",
          "li",
          "a",
          "table",
          "thead",
          "tbody",
          "tr",
          "th",
          "td",
        ];

        const cleanNode = (node: Node): Node | null => {
          if (node.nodeType === Node.TEXT_NODE) {
            return node.cloneNode(true);
          }

          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const tagName = element.tagName.toLowerCase();

            if (allowedTags.includes(tagName)) {
              const newElement = document.createElement(tagName);

              if (tagName === "a" && element.hasAttribute("href")) {
                newElement.setAttribute("href", element.getAttribute("href")!);
              }

              Array.from(element.childNodes).forEach((child) => {
                const cleanedChild = cleanNode(child);
                if (cleanedChild) {
                  newElement.appendChild(cleanedChild);
                }
              });

              return newElement;
            } else {
              const fragment = document.createDocumentFragment();
              Array.from(element.childNodes).forEach((child) => {
                const cleanedChild = cleanNode(child);
                if (cleanedChild) {
                  fragment.appendChild(cleanedChild);
                }
              });
              return fragment;
            }
          }

          return null;
        };

        const cleanedBody = document.createElement("div");
        Array.from(doc.body.childNodes).forEach((child) => {
          const cleanedChild = cleanNode(child);
          if (cleanedChild) {
            cleanedBody.appendChild(cleanedChild);
          }
        });

        const cleanedHTML = cleanedBody.innerHTML;

        return cleanedHTML;
      },
    },

    onUpdate: ({ editor }) => {
      let html = editor.getHTML();

      html = html.replace(/<p class="text-body"><\/p>/g, "<br>");
      html = html.replace(/<p[^>]*>\s*<\/p>/g, "<br>");

      previousValue.current = html;
      onChange(html);

      if (showSlots) {
        const slots = extractSlotsFromHTML(html);
        setUsedSlots(slots);
        onSlotsChange?.(slots);
      }
    },
  });

  useEffect(() => {
    if (editor && editorRef && "current" in editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  useEffect(() => {
    if (!showSlots) return;

    const handleOpenSlotModal = (event: CustomEvent) => {
      const { slotName, slotType, from, to } = event.detail;
      setSlotToEdit({
        name: slotName,
        type: slotType,
        from,
        to,
      });
      setIsSlotModalOpen(true);
    };

    document.addEventListener(
      "openSlotModal",
      handleOpenSlotModal as EventListener,
    );

    return () => {
      document.removeEventListener(
        "openSlotModal",
        handleOpenSlotModal as EventListener,
      );
    };
  }, [showSlots]);

  useEffect(() => {
    if (!editor) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousValue.current = value;

      return;
    }

    if (value !== previousValue.current && value !== editor.getHTML()) {
      editor.commands.setContent(value);
      previousValue.current = value;
    }
  }, [value, editor]);

  useEffect(() => {
    if (showSlots && value) {
      const slots = extractSlotsFromHTML(value);

      const hasChanges =
        slots.size !== usedSlots.size ||
        Array.from(slots.keys()).some(
          (key) => slots.get(key) !== usedSlots.get(key),
        );

      if (hasChanges) {
        setUsedSlots(slots);
        onSlotsChange?.(slots);
      }
    }
  }, [value, showSlots, onSlotsChange, usedSlots]);

  const extractSlotsFromHTML = (html: string): Map<string, SlotType> => {
    const slotPattern = /\{\{([a-z0-9_-]+)(?::([a-z]+))?\}\}/gi;
    const slots = new Map<string, SlotType>();
    let match;

    while ((match = slotPattern.exec(html)) !== null) {
      const name = match[1];
      const type = (match[2] as SlotType) || "string";
      slots.set(name, type);
    }

    return slots;
  };

  const handleInsertVariable = (variableName: string, type: SlotType) => {
    if (!editor) return;

    const slotText =
      type === "string"
        ? `{{${variableName}}} `
        : `{{${variableName}:${type}}} `;

    editor.chain().focus().insertContent(slotText).run();

    const currentPos = editor.state.selection.from;
    editor
      .chain()
      .insertContent(" ")
      .setTextSelection(currentPos + 1)
      .run();

    setUsedSlots((prev) => new Map([...prev, [variableName, type]]));
  };

  const handleUpdateSlot = (
    oldName: string,
    newName: string,
    newType: SlotType,
  ) => {
    if (!editor) return;

    const normalizedNewName = newName.trim().toLowerCase().replace(/\s+/g, "_");

    let html = editor.getHTML();

    const oldSlotPattern = new RegExp(
      `\\{\\{${oldName}(?::[a-z]+)?\\}\\}`,
      "gi",
    );

    const newSlot =
      newType === "string"
        ? `{{${normalizedNewName}}} `
        : `{{${normalizedNewName}:${newType}}} `;

    html = html.replace(oldSlotPattern, newSlot);
    // Agregar span para evitar quitar el estilo del último slot
    html = html.replace(
      /(\{\{[a-z0-9_-]+(?::[a-z]+)?\}\}\s*)(<\/p>(?:<\/[^>]+>)*)$/i,
      "$1<span>\u200B</span>$2",
    );

    editor.commands.setContent(html);
    onChange(html);

    setTimeout(() => {
      editor.commands.focus();
    }, 50);
  };

  const handleDeleteSlot = () => {
    if (!editor || !slotToEdit) return;

    editor.commands.deleteRange({
      from: slotToEdit.from,
      to:
        slotToEdit.to +
        (editor.state.doc.textBetween(slotToEdit.to, slotToEdit.to + 1) === " "
          ? 1
          : 0),
    });
  };

  const containerClass = integrated
    ? ""
    : "border border-neutral-quiet rounded-lg bg-white";

  return (
    <>
      {showSlots && (
        <SlotModal
          isOpen={isSlotModalOpen}
          onClose={() => {
            setIsSlotModalOpen(false);
            setSlotToEdit(null);
            setTimeout(() => {
              editor?.view.focus();
            }, 100);
          }}
          onInsert={handleInsertVariable}
          slotToEdit={slotToEdit}
          onUpdate={handleUpdateSlot}
          onDelete={handleDeleteSlot}
        />
      )}

      <div className={containerClass}>
        <div className="border-b border-neutral-quiet p-3 bg-neutral-silent flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ConfigurableMenuBar
              editor={editor}
              allowedOptions={
                showHeadings
                  ? [
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "link",
                      "h1",
                      "h2",
                      "h3",
                      "bulletList",
                      "orderedList",
                    ]
                  : [
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "link",
                      "bulletList",
                      "orderedList",
                    ]
              }
            />

            {showAlignment && editor && (
              <>
                <Button
                  onClick={() =>
                    editor.chain().focus().setTextAlign("left").run()
                  }
                  variant={
                    editor.isActive({ textAlign: "left" }) ? "default" : "ghost"
                  }
                  size="sm"
                  title="Alinear izquierda"
                >
                  <Icon type="far" name="align-left" />
                </Button>
                <Button
                  onClick={() =>
                    editor.chain().focus().setTextAlign("center").run()
                  }
                  variant={
                    editor.isActive({ textAlign: "center" })
                      ? "default"
                      : "ghost"
                  }
                  size="sm"
                  title="Centrar"
                >
                  <Icon type="far" name="align-center" />
                </Button>
                <Button
                  onClick={() =>
                    editor.chain().focus().setTextAlign("right").run()
                  }
                  variant={
                    editor.isActive({ textAlign: "right" })
                      ? "default"
                      : "ghost"
                  }
                  size="sm"
                  title="Alinear derecha"
                >
                  <Icon type="far" name="align-right" />
                </Button>
                <Button
                  onClick={() =>
                    editor.chain().focus().setTextAlign("justify").run()
                  }
                  variant={
                    editor.isActive({ textAlign: "justify" })
                      ? "default"
                      : "ghost"
                  }
                  size="sm"
                  title="Justificar"
                >
                  <Icon type="far" name="align-justify" />
                </Button>
              </>
            )}

            {showTables && editor && (
              <div className="flex items-center gap-1 ml-2 border-l border-neutral-quiet pl-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  }
                  disabled={!editor.can().insertTable()}
                  title="Insertar tabla"
                >
                  <Icon type="far" name="table" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().addColumnBefore().run()}
                  disabled={!editor.can().addColumnBefore()}
                  title="Agregar columna antes"
                >
                  <Icon type="far" name="table-columns" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().addRowBefore().run()}
                  disabled={!editor.can().addRowBefore()}
                  title="Agregar fila antes"
                >
                  <Icon type="far" name="table-rows" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                  disabled={!editor.can().deleteColumn()}
                  title="Eliminar columna"
                >
                  <Icon type="far" name="trash" className="text-danger" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().deleteRow().run()}
                  disabled={!editor.can().deleteRow()}
                  title="Eliminar fila"
                >
                  <Icon type="far" name="trash-xmark" className="text-danger" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => editor.chain().focus().deleteTable().run()}
                  disabled={!editor.can().deleteTable()}
                  title="Eliminar tabla"
                >
                  <Icon type="far" name="times" className="text-danger" />
                </Button>
              </div>
            )}

            {showSlots && (
              <Button
                variant="default"
                size="sm"
                className="flex items-center gap-2 ml-2"
                onClick={() => setIsSlotModalOpen(true)}
              >
                Variable
                <Icon type="far" name="plus" className="text-sm" />
              </Button>
            )}
          </div>
          {versiones && versiones.length > 0 && (
            <DropdownList
              options={versiones}
              selectedValue={
                versiones.find((v) => v.value === selectedVersion) ||
                versiones[0]
              }
              toggleSelection={(option) => {
                onVersionChange?.(option.value);
              }}
            />
          )}
        </div>

        {templateType === "email" && (
          <div className="border-b border-neutral-quiet px-6 py-4 bg-neutral-object">
            <div className="flex items-center gap-3">
              <Icon
                type="fas"
                name="paper-plane-top"
                className="text-body-quiet"
              />
              <input
                type="text"
                value={subjectValue}
                onChange={(e) => onSubjectChange?.(e.target.value)}
                placeholder="Escribe el asunto del correo..."
                className="flex-1 outline-none text-body-loud placeholder:text-body-quiet text-base font-semibold"
              />
            </div>
          </div>
        )}

        {/*{templateType === "document" && (
          <div className="border-b border-neutral-quiet px-6 py-4 bg-neutral-object">
             <div className="flex items-center gap-3">
              <Icon
                type="fas"
                name="file"
                className="text-body-quiet"
                size="xl"
              />

              {!fileNameValue ? (
                <input
                  type="text"
                  value={fileNameValue}
                  maxLength={50}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 50);
                    onFileNameChange?.(value);
                  }}
                  placeholder="Escribe el nombre del archivo..."
                  className="flex-1 outline-none text-body-loud placeholder:text-body-quiet text-base font-semibold"
                />
              ) : (
                <div className="flex items-baseline font-mono text-body-loud">
                  <input
                    type="text"
                    value={fileNameValue}
                    maxLength={50}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 50);
                      onFileNameChange?.(value);
                    }}
                    className="outline-none border-none p-0 bg-transparent text-base font-semibold"
                    style={{
                      width: `${Math.max(2, fileNameValue.length) * 0.6}em`,
                    }}
                    autoFocus
                  />
                  <span className="text-body-quiet">.pdf</span>
                </div>
              )}
            </div> 
          </div>
        )}*/}

        <EditorContent editor={editor} />
      </div>
    </>
  );
}
